import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import useUrlState from "@/hooks/useUrlState";

type Status = "success" | "fail" | "pending" | "scheduled";

interface HistoryItem {
  id: string;
  type: "SMS" | "LMS" | "MMS";
  recipient: string;
  message: string;
  status: Status;
  sentAt: string;
  count: number;
}

const statusConfig: Record<Status, { label: string; className: string; icon: string }> = {
  success: { label: "성공", className: "bg-emerald-500/15 text-emerald-400", icon: "ri-checkbox-circle-line" },
  fail: { label: "실패", className: "bg-red-500/15 text-red-400", icon: "ri-close-circle-line" },
  pending: { label: "발송 중", className: "bg-amber-500/15 text-amber-400", icon: "ri-loader-4-line" },
  scheduled: { label: "예약", className: "bg-violet-500/15 text-violet-400", icon: "ri-calendar-schedule-line" },
};

const typeConfig: Record<string, string> = {
  SMS: "bg-violet-500/20 text-violet-300",
  LMS: "bg-pink-500/20 text-pink-300",
  MMS: "bg-amber-500/20 text-amber-300",
};

function formatKST(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}.${mo}.${day} ${h}:${mi}`;
}

// 탭 복귀(리로드) 시 로딩 없이 마지막 내역을 즉시 보여주기 위한 캐시
const ITEMS_CACHE_KEY = "cache:user_dashboard_recent_history";

function readItemsCache(): HistoryItem[] | null {
  try {
    const raw = sessionStorage.getItem(ITEMS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HistoryItem[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeItemsCache(list: HistoryItem[]): void {
  try {
    sessionStorage.setItem(ITEMS_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패(용량 초과 등)는 무시
  }
}

export default function RecentHistory() {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const cachedItemsRef = useRef<HistoryItem[] | null>(readItemsCache());
  // 필터 상태를 주소(URL)에도 남겨 새로고침·링크 공유에도 유지 (sessionStorage 백업)
  const [filter, setFilter] = useUrlState("dash_hist_filter", "all");
  const [items, setItems] = useState<HistoryItem[]>(cachedItemsRef.current ?? []);
  const [loading, setLoading] = useState(cachedItemsRef.current === null);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!user || !session) return;
    // 캐시된 내역이 있으면 로딩 화면 없이 조용히 갱신
    if (cachedItemsRef.current === null) setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("get-user-recent-history", {
        body: { user_id: user.id },
      });

      if (fnError) {
        console.warn("[RecentHistory] Edge Function error:", fnError);
        if (cachedItemsRef.current === null) setItems([]);
        setError("내역을 불러오지 못했습니다.");
        return;
      }

      if (data?.error) {
        console.error("[RecentHistory] Edge Function error:", data.error);
        if (cachedItemsRef.current === null) setItems([]);
        setError("내역을 불러오지 못했습니다.");
      } else if (data?.history) {
        const mapped: HistoryItem[] = data.history.map((row: any) => {
          const recipients = row.send_recipients ?? [];
          let recipientLabel = "";
          if (recipients.length === 1) {
            recipientLabel = recipients[0].name ?? recipients[0].phone ?? "1명";
          } else if (recipients.length > 1) {
            recipientLabel = `${recipients.length.toLocaleString()}명`;
          } else {
            recipientLabel = `${row.total_count ?? 0}명`;
          }
          return {
            id: row.id,
            type: row.type,
            recipient: recipientLabel,
            message: row.message,
            status: (row.status === "failed" ? "fail" : row.status) as Status,
            sentAt: formatKST(row.sent_at),
            count: recipients.length || row.total_count || 0,
          };
        });
        setItems(mapped);
        writeItemsCache(mapped);
      } else {
        if (cachedItemsRef.current === null) setItems([]);
      }
    } catch (err) {
      console.error("[RecentHistory] fetch exception:", err);
      if (cachedItemsRef.current === null) setItems([]);
      setError("내역을 불러오지 못했습니다.");
    } finally {
      cachedItemsRef.current = cachedItemsRef.current ?? [];
      setLoading(false);
    }
  }, [user, session]);

  useEffect(() => {
    if (user && session) fetchHistory();
  }, [user, session, fetchHistory]);

  const filtered = filter === "all" ? items : items.filter((h) => h.status === filter);

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-white/[0.07]">
        <div>
          <h3 className="text-white font-semibold text-base">최근 발송 내역</h3>
          <p className="text-gray-500 text-xs mt-0.5">최근 30일 기준</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {(["all", "success", "fail", "scheduled"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                filter === f
                  ? "bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                  : "bg-white/[0.05] text-gray-400 hover:text-white hover:bg-white/[0.08]"
              }`}
            >
              {f === "all" ? "전체" : statusConfig[f].label}
            </button>
          ))}
        </div>
      </div>

      {/* Table - Desktop only */}
      {loading ? (
        <div className="py-12 text-center">
          <div className="w-8 h-8 border-2 border-violet-500/40 border-t-violet-500 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">불러오는 중...</p>
        </div>
      ) : error && items.length === 0 ? (
        <div className="py-12 text-center">
          <i className="ri-error-warning-line text-3xl text-red-400 mb-2 block"></i>
          <p className="text-red-400 text-sm mb-3">{error}</p>
          <button
            onClick={fetchHistory}
            className="px-4 py-2 bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 rounded-lg text-sm cursor-pointer transition-colors"
          >
            다시 시도
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-white/[0.04]">
            <i className="ri-send-plane-line text-gray-600 text-3xl"></i>
          </div>
          <p className="text-gray-400 text-base font-medium mb-1">아직 발송 내역이 없어요</p>
          <p className="text-gray-600 text-sm mb-5">첫 문자를 발송해보세요!</p>
          <button
            onClick={() => navigate("/dashboard/send")}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-pink-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-send-plane-line"></i>
            문자 발송하기
          </button>
        </div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.05]">
                  <th className="text-left text-gray-500 text-xs font-medium px-6 py-3 whitespace-nowrap">유형</th>
                  <th className="text-left text-gray-500 text-xs font-medium px-4 py-3 whitespace-nowrap">수신자</th>
                  <th className="text-left text-gray-500 text-xs font-medium px-4 py-3 whitespace-nowrap">메시지</th>
                  <th className="text-left text-gray-500 text-xs font-medium px-4 py-3 whitespace-nowrap">건수</th>
                  <th className="text-left text-gray-500 text-xs font-medium px-4 py-3 whitespace-nowrap">상태</th>
                  <th className="text-left text-gray-500 text-xs font-medium px-4 py-3 whitespace-nowrap">발송 시각</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => (
                  <tr
                    key={item.id}
                    className={`border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors ${
                      idx === filtered.length - 1 ? "border-b-0" : ""
                    }`}
                  >
                    <td className="px-6 py-3.5">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-md whitespace-nowrap ${typeConfig[item.type]}`}>
                        {item.type}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-gray-300 text-sm whitespace-nowrap">{item.recipient}</span>
                    </td>
                    <td className="px-4 py-3.5 max-w-[260px]">
                      <p className="text-gray-400 text-sm truncate">{item.message}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-gray-300 text-sm whitespace-nowrap">{item.count.toLocaleString()}건</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${statusConfig[item.status].className}`}>
                        <i className={`${statusConfig[item.status].icon} text-xs`}></i>
                        {statusConfig[item.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-gray-500 text-xs whitespace-nowrap">{item.sentAt}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Card list - Mobile only */}
          <div className="md:hidden divide-y divide-white/[0.05]">
            {filtered.map((item) => (
              <div key={item.id} className="px-4 py-3.5 flex items-center gap-3">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md whitespace-nowrap shrink-0 ${typeConfig[item.type]}`}>
                    {item.type}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-300 text-sm font-medium truncate">{item.recipient}</span>
                      <span className="text-gray-500 text-xs shrink-0">{item.count.toLocaleString()}건</span>
                    </div>
                    <p className="text-gray-400 text-xs truncate mt-0.5">{item.message}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${statusConfig[item.status].className}`}>
                    <i className={`${statusConfig[item.status].icon} text-[10px]`}></i>
                    {statusConfig[item.status].label}
                  </span>
                  <span className="text-gray-500 text-[10px] whitespace-nowrap">{item.sentAt}</span>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-12 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-inbox-line text-gray-600 text-3xl"></i>
              </div>
              <p className="text-gray-500 text-sm">해당 조건의 발송 내역이 없습니다.</p>
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-3 border-t border-white/[0.07] flex items-center justify-between">
            <p className="text-gray-600 text-xs">총 {filtered.length}건</p>
            <button
              onClick={() => navigate("/dashboard/history")}
              className="text-violet-400 hover:text-violet-300 text-xs cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1"
            >
              전체 내역 보기
              <i className="ri-arrow-right-line text-xs"></i>
            </button>
          </div>
        </>
      )}
    </div>
  );
}